import { useState, useRef } from 'react'
import { logEvent } from '../../store/eventLog.js'
import { todayStr } from '../../data/seed.js'
import { sbDispararAjusteOmie, forceSyncAllToSupabase } from '../../store/products.js'
import { supabase } from '../../lib/supabase.js'

// ── Helpers ──────────────────────────────────────────────────────────────────
function genCRSku(n){const s=String(n);const pad=Math.max(4,s.length);return"CR"+s.padStart(pad,"0");}

function nextCRNum(products){
  const nums=products.map(p=>{const m=p.sku.match(/^CR(\d+)$/);return m?parseInt(m[1]):0;});
  return nums.length>0?Math.max(...nums):0;
}

function normalizeStr(s){
  return s.trim().toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g,"")
    .replace(/\s+/g," ");
}

function levenshtein(a,b){
  const m=a.length,n=b.length;
  const dp=Array.from({length:m+1},(_,i)=>Array.from({length:n+1},(_,j)=>i===0?j:j===0?i:0));
  for(let i=1;i<=m;i++)for(let j=1;j<=n;j++)
    dp[i][j]=a[i-1]===b[j-1]?dp[i-1][j-1]:1+Math.min(dp[i-1][j],dp[i][j-1],dp[i-1][j-1]);
  return dp[m][n];
}

function similarity(a,b){
  const na=normalizeStr(a),nb=normalizeStr(b);
  const maxLen=Math.max(na.length,nb.length);
  if(maxLen===0)return 1;
  return 1-levenshtein(na,nb)/maxLen;
}

function fmtTs(ts){
  if(!ts)return"—";
  const d=new Date(ts);
  return d.toLocaleString("pt-BR",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"});
}

// ── Parser XLSX via SheetJS (CDN) ─────────────────────────────────────────────
async function parseXLSX(file){
  return new Promise((resolve,reject)=>{
    const reader=new FileReader();
    reader.onload=e=>{
      try{
        const XLSX=window.XLSX;
        if(!XLSX)throw new Error("SheetJS não carregado");
        const wb=XLSX.read(e.target.result,{type:"array"});
        const ws=wb.Sheets[wb.SheetNames[0]];
        const rows=XLSX.utils.sheet_to_json(ws,{defval:""});
        resolve(rows);
      }catch(err){reject(err);}
    };
    reader.onerror=()=>reject(new Error("Erro ao ler arquivo"));
    reader.readAsArrayBuffer(file);
  });
}

// ── Normalizar linha da planilha ───────────────────────────────────────────────
function normalizeRow(row){
  // Aceita variações de header: DESCRICAO, Descrição, nome, NAME, etc.
  const keys=Object.keys(row);
  const get=(...candidates)=>{
    for(const c of candidates){
      const k=keys.find(k=>normalizeStr(k)===normalizeStr(c));
      if(k!==undefined){
        const v=row[k];
        return v===null||v===undefined?"":String(v).trim();
      }
    }
    return "";
  };
  return{
    name:      get("descricao","descrição","nome","name","produto","description"),
    qtd_croma:get("qtd_croma","qtd_croma","croma","qty_croma","quant_croma","quantidade_croma"),
    qtd_esfera:get("qtd_esfera","qtd_esfera","esfera","qty_esfera","quant_esfera","quantidade_esfera"),
    price:     get("preco","preço","price","valor","valor_unitario"),
    sku:       get("sku","cod","codigo","código","code","sku_cr"),
    status:    get("status","inventario_status"),
    ncm:       get("ncm","cod_ncm","codigo_ncm"),
    empresa:   get("empresa","cnpj","minha_empresa"),
    endereco:  get("endereco","endereço","localizacao","localização"),
  };
}

// ── Validar linhas ─────────────────────────────────────────────────────────────
function validateRows(rows){
  const erros=[];
  const validas=[];
  rows.forEach((row,i)=>{
    const n=normalizeRow(row);
    const linha=i+2; // +2 = header + 1-based
    if(!n.name){erros.push({linha,msg:"DESCRICAO ausente"});return;}
    const croma=parseInt(n.qtd_croma);
    const esfera=parseInt(n.qtd_esfera);
    if(isNaN(croma)||croma<0){erros.push({linha,msg:`QTD_CROMA inválido: "${n.qtd_croma}"`});return;}
    if(isNaN(esfera)||esfera<0){erros.push({linha,msg:`QTD_ESFERA inválido: "${n.qtd_esfera}"`});return;}
    const price=n.price===""?0:parseFloat(String(n.price).replace(",","."));
    validas.push({
      name:n.name,
      croma,
      esfera,
      price:isNaN(price)?0:price,
      sku_fornecedor:n.sku||"",
      ncm:n.ncm||"",
      empresa:n.empresa||"",
      endereco:n.endereco||"",
      inventario_status:n.status||"ok",
    });
  });
  return{validas,erros};
}

// ── SKU helpers (duplicados de modals.jsx — evita import circular) ────────────
const _ALIAS_MAP={"CRO EQUIP":"EQP","CRO TI":"TI","CRO DIST":"DST","ESFERA":"ESF","CFM DIST":"CFM","M.A. DIST":"MAM","CRUZEIRO":"CRZ","CRO TECINFO":"TEC","ESPACO":"ESP","C.A.S.":"CAS"};
const _genAlias=n=>_ALIAS_MAP[n]||"EQP";
const _genDateStr=()=>{const d=new Date();return String(d.getDate()).padStart(2,"0")+String(d.getMonth()+1).padStart(2,"0")+d.getFullYear();};

// ── Detectar similares (Fluxo 3) ──────────────────────────────────────────────
function detectarSimilares(rows,products){
  const existingNames=products.map(p=>p.name);
  return rows.map(row=>{
    let melhor={score:0,nome:""};
    for(const nome of existingNames){
      const s=similarity(row.name,nome);
      if(s>melhor.score){melhor={score:s,nome};}
    }
    return{...row,_similar:melhor.score>=0.8?melhor:null};
  });
}

// ── Histórico de importações (localStorage) ───────────────────────────────────
const LS_HIST="estoqueos_import_history";
function loadHistory(){
  try{const r=localStorage.getItem(LS_HIST);return r?JSON.parse(r):[];}
  catch{return[];}
}
function saveHistory(h){
  try{localStorage.setItem(LS_HIST,JSON.stringify(h.slice(0,50)));}catch{}
}
function appendHistory(entry){
  const h=loadHistory();
  saveHistory([entry,...h]);
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
export default function PlanilhaModule({user,products,setProducts}){
  const [step,setStep]=useState("home"); // home | preview_f1 | confirm_f1 | preview_f2 | bloqueio_f2 | preview_f3
  const [rows,setRows]=useState([]);
  const [erros,setErros]=useState([]);
  const [history,setHistory]=useState(loadHistory);
  const [loading,setLoading]=useState(false);
  const [syncState,setSyncState]=useState("idle"); // idle | syncing | done | error
  const [erro,setErro]=useState("");
  const [f2Meta,setF2Meta]=useState(null); // {exportTs, ausentes, alterados}
  const fileRef=useRef(null);
  const fileRef2=useRef(null);
  const fileRef3=useRef(null);

  // ── Upload e parse ─────────────────────────────────────────────────────────
  async function handleFile(file,fluxo){
    if(!file)return;
    if(!file.name.endsWith(".xlsx")){setErro("Apenas arquivos .xlsx são aceitos.");return;}
    setErro("");setLoading(true);
    try{
      // Garantir SheetJS carregado
      if(!window.XLSX){
        await new Promise((res,rej)=>{
          const s=document.createElement("script");
          s.src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
          s.onload=res;s.onerror=()=>rej(new Error("Falha ao carregar SheetJS"));
          document.head.appendChild(s);
        });
      }
      const rawRows=await parseXLSX(file);
      if(rawRows.length===0){setErro("Planilha vazia ou sem dados reconhecíveis.");setLoading(false);return;}
      const{validas,erros:errsFound}=validateRows(rawRows);
      setErros(errsFound);
      if(fluxo===1){
        const sorted=[...validas].sort((a,b)=>a.name.localeCompare(b.name,"pt-BR",{sensitivity:"base"}));
        const dateStr=_genDateStr();
        const counters={};
        sorted.forEach(r=>{
            const alias=_genAlias(r.empresa);
            if(!counters[alias])counters[alias]=1;
            r.sku=`${alias}-${dateStr}-${String(counters[alias]++).padStart(5,"0")}`;
        });
        setRows(sorted);
        setStep("preview_f1");
      } else if(fluxo===2){
        // Fluxo 2 — reimportação com validação de timestamp
        // Ler timestamp da aba _META
        if(!window.XLSX){throw new Error("SheetJS não carregado");}
        const XLSX=window.XLSX;
        const wb2=await new Promise((res,rej)=>{
          const r2=new FileReader();
          r2.onload=e2=>{try{res(XLSX.read(e2.target.result,{type:"array"}));}catch(err){rej(err);}};
          r2.onerror=()=>rej(new Error("Erro ao ler arquivo"));
          // Re-read file - já parseado acima, usar rawRows mas precisamos do wb para _META
          // Vamos usar file novamente
          r2.readAsArrayBuffer(file);
        });
        let exportTs=null;
        if(wb2.SheetNames.includes("_META")){
          const metaRows=XLSX.utils.sheet_to_json(wb2.Sheets["_META"],{defval:""});
          if(metaRows.length>0)exportTs=metaRows[0].EXPORT_TIMESTAMP||null;
        }
        if(!exportTs){setErro("Planilha sem metadados de exportação. Use apenas arquivos exportados pelo EstoqueOS.");setLoading(false);return;}
        // Verificar se houve movimentação manual após o timestamp de exportação
        const tsExport=new Date(exportTs).getTime();
        const exportDate=exportTs.slice(0,10); // "2026-03-10"
        const movApos=products.some(p=>(p.movements||[]).some(mv=>{
            return mv.date&&mv.date>exportDate;
        }));
        // Calcular diff: alterados e ausentes
        const skuMap={};
        products.forEach(p=>{skuMap[p.sku]=p;});
        const skusNaPlanilha=new Set();
        const alterados=[];
        validas.forEach(r=>{
          // Fluxo 2 precisa do SKU — pegar do campo sku_fornecedor (que na exportação é o SKU)
          const skuPlanilha=r.sku_fornecedor;
          if(!skuPlanilha)return;
          skusNaPlanilha.add(skuPlanilha);
          const prod=skuMap[skuPlanilha];
          if(!prod)return;
          const diffs=[];
          if(r.name!==prod.name)diffs.push({campo:"DESCRICAO",de:prod.name,para:r.name});
          if(r.croma!==prod.croma)diffs.push({campo:"QTD_CROMA",de:prod.croma,para:r.croma});
          if(r.esfera!==prod.esfera)diffs.push({campo:"QTD_ESFERA",de:prod.esfera,para:r.esfera});
          if(Math.abs((r.price||0)-(prod.price||0))>0.001)diffs.push({campo:"PRECO",de:prod.price,para:r.price});
          if((r.endereco||"")!==(prod.endereco||""))diffs.push({campo:"ENDERECO",de:prod.endereco||"",para:r.endereco||""});
          if(diffs.length>0)alterados.push({sku:skuPlanilha,name:prod.name,diffs,rowData:r});
        });
        const ausentes=products.filter(p=>!skusNaPlanilha.has(p.sku)).map(p=>({...p,_ausente:true}));
        setF2Meta({exportTs,movApos,ausentes,alterados});
        setRows(alterados);
        if(movApos){
          setStep("bloqueio_f2");
        } else {
          setStep("preview_f2");
        }
      } else {
        // Fluxo 3 — incluir em massa, detectar similares
        const base=nextCRNum(products);
        const comSku=validas.map((r,i)=>({...r,sku:genCRSku(base+i+1)}));
        const comSimilar=detectarSimilares(comSku,products);
        setRows(comSimilar);
        setStep("preview_f3");
      }
    }catch(e){setErro("Erro ao processar planilha: "+e.message);}
    setLoading(false);
  }

  // ── Confirmar importação inicial (Fluxo 1) ─────────────────────────────────
  async function confirmarFluxo1(){
    const ts=new Date().toISOString();
    const novos=rows.map(r=>({
      id:crypto.randomUUID(),
      sku:r.sku,
      sku_fornecedor:r.sku_fornecedor||"",
      name:r.name,
      price:r.price,
      croma:r.croma,
      esfera:r.esfera,
      fisico:r.croma+r.esfera,
      cnpj:r.empresa||"",
      ncm:r.ncm||"",
      endereco:r.endereco||"",
      unidade:"UN",
      movements:[],
      editHistory:[],
      inventario_status:"ok",
      inventario_bloqueado:false,
      ultima_importacao:ts,
      created_at:todayStr(),
    }));
    setProducts(()=>novos);
    logEvent({
      tipo_evento:"IMPORTACAO_INVENTARIO",
      usuario:user.name,
      entidade:"SISTEMA",
      id_entidade:"PLANILHA",
      detalhes:`${novos.length} itens importados — inventário inicial definitivo`,
    });
    const entry={tipo:"IMPORTACAO_INVENTARIO",data:ts,itens:novos.length,usuario:user.name};
    appendHistory(entry);
    setHistory(loadHistory());
    // Alinha o corte do sync Omie com o timestamp do import
    const inicioDia=new Date();inicioDia.setHours(0,0,0,0);
    await supabase.from('config').upsert({key:'omie_sync_activated_at',value:ts},{onConflict:'key'});
    await supabase.from('config').upsert({key:'omie_last_check',value:inicioDia.toISOString()},{onConflict:'key'});
    await supabase.from('config').upsert({key:'nfs_processadas',value:'[]'},{onConflict:'key'});
    setStep('home');
    setRows([]);
    setErros([]);
  }

  // ── Confirmar inclusão em massa (Fluxo 3) ─────────────────────────────────
  function confirmarFluxo3(){
    const ts=new Date().toISOString();
    const aprovados=rows.filter(r=>!r._bloqueado);
    const novos=aprovados.map(r=>({
      id:crypto.randomUUID(),
      sku:r.sku,
      sku_fornecedor:r.sku_fornecedor||"",
      name:r.name,
      price:r.price,
      croma:r.croma,
      esfera:r.esfera,
      fisico:r.croma+r.esfera,
      ncm:r.ncm||"",               // <-- SALVANDO O NCM AQUI
      cnpj:r.empresa||"CRO EQUIP", // <-- SALVANDO A EMPRESA AQUI (Default CRO EQUIP)
      movements:[],
      editHistory:[],
      inventario_status:"ok",
      inventario_bloqueado:false,
      ultima_importacao:ts,
      created_at:todayStr(),
    }));
    setProducts(prev=>[...prev,...novos]);
    logEvent({
      tipo_evento:"IMPORTACAO_MASSA",
      usuario:user.name,
      entidade:"SISTEMA",
      id_entidade:"PLANILHA",
      detalhes:`${novos.length} novos itens incluídos em massa`,
    });
    const entry={tipo:"IMPORTACAO_MASSA",data:ts,itens:novos.length,usuario:user.name};
    appendHistory(entry);
    setHistory(loadHistory());
    setStep("home");
    setRows([]);
    setErros([]);
  }

  // ── Confirmar reimportação (Fluxo 2) ──────────────────────────────────────
  function confirmarFluxo2(){
    const ts=new Date().toISOString();
    const aprovados=rows.filter(r=>!r._bloqueado);
    setProducts(prev=>{
      const map={};prev.forEach(p=>{map[p.sku]=p;});
      aprovados.forEach(r=>{
        const prod=map[r.sku];if(!prod)return;
        const h=prod.editHistory||[];
        const entry={date:todayStr(),fonte:"REIMPORT_MASSA"};
        r.diffs.forEach(d=>{entry[d.campo]=`${d.de}→${d.para}`;});
        const deltaFisico=(r.rowData.croma+r.rowData.esfera)-(prod.croma+prod.esfera);
        if(deltaFisico!==0){
          sbDispararAjusteOmie(prod.id,{type:'ajuste',delta:deltaFisico,note:"Reimportação em Massa OS"});
        }
        const updated={...prod,
          name:r.rowData.name,
          croma:r.rowData.croma,
          esfera:r.rowData.esfera,
          fisico:r.rowData.croma+r.rowData.esfera,
          price:r.rowData.price,
          endereco:r.rowData.endereco||prod.endereco||"",
          inventario_status:r.rowData.inventario_status||prod.inventario_status||"ok",
          editHistory:[entry,...h],
          ultima_importacao:ts,
        };
        map[r.sku]=updated;
      });
      // Marcar ausentes
      if(f2Meta?.ausentes){
        f2Meta.ausentes.forEach(p=>{
          if(map[p.sku]){
            if(map[p.sku].inventario_status==="arquivado")return;
            map[p.sku]={...map[p.sku],inventario_status:"nao_contabilizado",inventario_bloqueado:true};
            logEvent({tipo_evento:"ITEM_NAO_CONTABILIZADO",usuario:user.name,entidade:"PRODUTO",id_entidade:p.sku,detalhes:`Item ausente na reimportação — movimentações bloqueadas`});
          }
        });
      }
      return prev.map(p=>map[p.sku]||p);
    });
    aprovados.forEach(r=>{
      logEvent({tipo_evento:"REIMPORT_MASSA",usuario:user.name,entidade:"PRODUTO",id_entidade:r.sku,
        detalhes:r.diffs.map(d=>`${d.campo}: ${d.de}→${d.para}`).join(" | ")});
    });
    logEvent({tipo_evento:"REIMPORTACAO",usuario:user.name,entidade:"SISTEMA",id_entidade:"PLANILHA",
      detalhes:`${aprovados.length} itens atualizados | ${f2Meta?.ausentes?.length||0} ausentes marcados`});
    const entry={tipo:"REIMPORTACAO",data:ts,itens:aprovados.length,usuario:user.name};
    appendHistory(entry);
    setHistory(loadHistory());
    setStep("home");setRows([]);setErros([]);setF2Meta(null);
  }

  function cancelar(){setStep("home");setRows([]);setErros([]);setErro("");setF2Meta(null);}

  // ── Exportar XLSX (Fluxo 2) ────────────────────────────────────────────────
  async function exportar(){
    setErro("");setLoading(true);
    try{
      if(!window.XLSX){
        await new Promise((res,rej)=>{
          const s=document.createElement("script");
          s.src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
          s.onload=res;s.onerror=()=>rej(new Error("Falha ao carregar SheetJS"));
          document.head.appendChild(s);
        });
      }
      const XLSX=window.XLSX;
      const ts=new Date().toISOString();
      // Aba principal
      const dados=products.map(p=>({
        SKU:p.sku,
        DESCRICAO:p.name,
        QTD_CROMA:p.croma||0,
        QTD_ESFERA:p.esfera||0,
        PRECO:p.price||0,
        ENDERECO:p.endereco||"",  // <-- ADICIONADO NA EXPORTAÇÃO
        NCM:p.ncm||"",            // <-- NCM ADICIONADO AQUI
        EMPRESA:p.cnpj||"CRO TI", // <-- COLUNA ADICIONADA AQUI
        STATUS:p.inventario_status||"ok",
      }));
      const ws=XLSX.utils.json_to_sheet(dados);
      // Proteger coluna SKU — largura fixa e nota
      ws["!cols"]=[{wch:12},{wch:50},{wch:12},{wch:12},{wch:12},{wch:20},{wch:12},{wch:14},{wch:12}];

      // Aba de metadados oculta com timestamp
      const wsMeta=XLSX.utils.json_to_sheet([{EXPORT_TIMESTAMP:ts,TOTAL_ITENS:products.length,EXPORTADO_POR:user.name}]);

      const wb=XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb,ws,"ESTOQUE");
      XLSX.utils.book_append_sheet(wb,wsMeta,"_META");

      const nome=`EstoqueOS_Export_${ts.slice(0,10)}.xlsx`;
      XLSX.writeFile(wb,nome);

      logEvent({tipo_evento:"EXPORTACAO_PLANILHA",usuario:user.name,entidade:"SISTEMA",id_entidade:"PLANILHA",detalhes:`${products.length} itens exportados`});
      const entry={tipo:"EXPORTACAO_PLANILHA",data:ts,itens:products.length,usuario:user.name};
      appendHistory(entry);
      setHistory(loadHistory());
    }catch(e){setErro("Erro ao exportar: "+e.message);}
    setLoading(false);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return(
    <div style={{flex:1,overflowY:"auto",padding:"28px 20px"}}>
      <div style={{maxWidth:860,margin:"0 auto"}}>

        {/* Título */}
        <div style={{marginBottom:24}}>
          <p style={{fontSize:9,letterSpacing:3,color:"var(--text4)",marginBottom:4}}>GESTAO DE PLANILHAS</p>
          <h2 style={{fontSize:16,fontWeight:700,color:"var(--text)",letterSpacing:2,fontFamily:"'Courier New',monospace",margin:0}}>
            IMPORTACAO / EXPORTACAO
          </h2>
        </div>

        {/* Erro global */}
        {erro&&<div style={{background:"#ff475715",border:"1px solid #ff475744",borderRadius:2,padding:"10px 14px",marginBottom:16,fontSize:10,color:"#ff4757",letterSpacing:1}}>{erro}</div>}

        {/* ── HOME ─────────────────────────────────────────────────────────── */}
        {step==="home"&&<>
          {/* Botões de ação */}
          <div style={{display:"flex",gap:10,marginBottom:32,flexWrap:"wrap"}}>

            {/* Fluxo 1 — Importação Inicial */}
            <div style={{flex:1,minWidth:220,background:"var(--bg2)",border:"1px solid var(--border2)",borderRadius:2,padding:"18px 16px"}}>
              <p style={{fontSize:8,letterSpacing:3,color:"#00d084",marginBottom:6}}>FLUXO 1</p>
              <p style={{fontSize:11,fontWeight:700,color:"var(--text)",marginBottom:6,letterSpacing:1}}>IMPORTAR INVENTARIO</p>
              <p style={{fontSize:9,color:"var(--text4)",lineHeight:1.6,marginBottom:14}}>
                Importa a planilha-mãe como verdade absoluta do sistema. Substitui todos os produtos existentes.
              </p>
              <input ref={fileRef} type="file" accept=".xlsx" style={{display:"none"}}
                onChange={e=>{handleFile(e.target.files[0],1);e.target.value="";}}/>
              <button onClick={()=>fileRef.current?.click()}
                disabled={loading}
                style={{background:"#00d08420",border:"1px solid #00d08455",color:"#00d084",padding:"8px 16px",fontSize:9,letterSpacing:2,cursor:loading?"not-allowed":"pointer",borderRadius:2,fontFamily:"'Courier New',monospace",width:"100%"}}>
                {loading?"PROCESSANDO...":"↑ SELECIONAR ARQUIVO"}
              </button>
            </div>

            {/* Fluxo 2 — Exportar + Reimportar */}
            <div style={{flex:1,minWidth:220,background:"var(--bg2)",border:"1px solid var(--border2)",borderRadius:2,padding:"18px 16px"}}>
              <p style={{fontSize:8,letterSpacing:3,color:"#3b82f6",marginBottom:6}}>FLUXO 2</p>
              <p style={{fontSize:11,fontWeight:700,color:"var(--text)",marginBottom:6,letterSpacing:1}}>EXPORTAR / REIMPORTAR</p>
              <p style={{fontSize:9,color:"var(--text4)",lineHeight:1.6,marginBottom:14}}>
                Exporte, edite em massa no Excel e reimporte. O sistema valida o timestamp e bloqueia se houve movimentações no intervalo.
              </p>
              <input ref={fileRef2} type="file" accept=".xlsx" style={{display:"none"}}
                onChange={e=>{handleFile(e.target.files[0],2);e.target.value="";}}/>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                <button onClick={exportar}
                  disabled={loading||products.length===0}
                  style={{background:"#3b82f620",border:"1px solid #3b82f655",color:"#3b82f6",padding:"8px 16px",fontSize:9,letterSpacing:2,cursor:(loading||products.length===0)?"not-allowed":"pointer",borderRadius:2,fontFamily:"'Courier New',monospace",width:"100%",opacity:products.length===0?0.5:1}}>
                  {loading?"PROCESSANDO...":"↓ EXPORTAR .XLSX"}
                </button>
                <button onClick={()=>fileRef2.current?.click()}
                  disabled={loading||products.length===0}
                  style={{background:"none",border:"1px solid #3b82f644",color:"#3b82f6",padding:"8px 16px",fontSize:9,letterSpacing:2,cursor:(loading||products.length===0)?"not-allowed":"pointer",borderRadius:2,fontFamily:"'Courier New',monospace",width:"100%",opacity:products.length===0?0.5:1}}>
                  {loading?"PROCESSANDO...":"↑ REIMPORTAR .XLSX"}
                </button>
              </div>
            </div>

            {/* Fluxo 3 — Incluir em Massa */}
            <div style={{flex:1,minWidth:220,background:"var(--bg2)",border:"1px solid var(--border2)",borderRadius:2,padding:"18px 16px"}}>
              <p style={{fontSize:8,letterSpacing:3,color:"#a78bfa",marginBottom:6}}>FLUXO 3</p>
              <p style={{fontSize:11,fontWeight:700,color:"var(--text)",marginBottom:6,letterSpacing:1}}>INCLUIR EM MASSA</p>
              <p style={{fontSize:9,color:"var(--text4)",lineHeight:1.6,marginBottom:14}}>
                Inclui novos itens em lote sem afetar o estoque existente. Detecta similares automaticamente.
              </p>
              <input ref={fileRef3} type="file" accept=".xlsx" style={{display:"none"}}
                onChange={e=>{handleFile(e.target.files[0],3);e.target.value="";}}/>
              <button onClick={()=>fileRef3.current?.click()}
                disabled={loading||products.length===0}
                style={{background:"#a78bfa20",border:"1px solid #a78bfa55",color:"#a78bfa",padding:"8px 16px",fontSize:9,letterSpacing:2,cursor:(loading||products.length===0)?"not-allowed":"pointer",borderRadius:2,fontFamily:"'Courier New',monospace",width:"100%",opacity:products.length===0?0.5:1}}>
                {loading?"PROCESSANDO...":"↑ SELECIONAR ARQUIVO"}
              </button>
            </div>
          </div>

          {/* Formato esperado */}
          <div style={{background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:2,padding:"14px 16px",marginBottom:24}}>
            <p style={{fontSize:8,letterSpacing:3,color:"var(--text4)",marginBottom:10}}>FORMATO ESPERADO DA PLANILHA</p>
            <div style={{display:"flex",gap:0,overflowX:"auto"}}>
            {["DESCRICAO *","QTD_CROMA *","QTD_ESFERA *","PRECO", "NCM", "EMPRESA", "ENDERECO"].map((col,i)=>(
                <div key={i} style={{flex:1,minWidth:100,padding:"6px 10px",background:i%2===0?"var(--bg)":"var(--bg3)",borderRadius:i===0?2:0}}>
                  <p style={{fontSize:8,letterSpacing:1,color:col.includes("*")?"#ffa502":"var(--text4)",fontFamily:"'Courier New',monospace",margin:0}}>{col}</p>
                </div>
              ))}
            </div>
            <p style={{fontSize:8,color:"var(--text5)",marginTop:8}}>* obrigatório — PRECO opcional, ausência gera badge SEM PRECO</p>
          </div>

          {/* Histórico */}
          {history.length>0&&<>
            <p style={{fontSize:9,letterSpacing:3,color:"var(--text4)",marginBottom:12}}>HISTORICO DE OPERACOES</p>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {history.map((h,i)=>{
                const color=h.tipo==="IMPORTACAO_INVENTARIO"?"#00d084":h.tipo==="EXPORTACAO_PLANILHA"?"#3b82f6":"#a78bfa";
                const icon=h.tipo==="EXPORTACAO_PLANILHA"?"↓":"↑";
                return(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 12px",background:"var(--bg2)",border:"1px solid var(--border)",borderRadius:2}}>
                    <span style={{fontSize:12,color,flexShrink:0}}>{icon}</span>
                    <span style={{fontSize:9,letterSpacing:2,color,fontWeight:700,flexShrink:0,minWidth:160}}>{h.tipo.replace(/_/g," ")}</span>
                    <span style={{fontSize:9,color:"var(--text3)",flex:1}}>{h.itens} itens</span>
                    <span style={{fontSize:8,color:"var(--text5)",whiteSpace:"nowrap"}}>{fmtTs(h.data)}</span>
                    <span style={{fontSize:8,color:"var(--text5)",whiteSpace:"nowrap"}}>{h.usuario}</span>
                  </div>
                );
              })}
            </div>
          </>}
          {/* Sync Supabase */}
          <div style={{flex:1,minWidth:220,background:"var(--bg2)",border:`1px solid ${syncState==="done"?"#00d08455":syncState==="error"?"#ff475755":"var(--border2)"}`,borderRadius:2,padding:"18px 16px"}}>
            <p style={{fontSize:8,letterSpacing:3,color:"#ffa502",marginBottom:6}}>MANUTENCAO</p>
            <p style={{fontSize:11,fontWeight:700,color:"var(--text)",marginBottom:6,letterSpacing:1}}>SINCRONIZAR SUPABASE</p>
            <p style={{fontSize:9,color:"var(--text4)",lineHeight:1.6,marginBottom:14}}>
              Força o envio de todos os {products.length} produtos do localStorage para o Supabase. Use após importações que não persistiram remotamente.
            </p>
            <button
              onClick={async()=>{
                setSyncState("syncing");
                try{
                  await forceSyncAllToSupabase();
                  setSyncState("done");
                }catch(e){
                  setSyncState("error");
                }
              }}
              disabled={syncState==="syncing"}
              style={{background:syncState==="done"?"#00d08420":syncState==="error"?"#ff475720":"#ffa50220",border:`1px solid ${syncState==="done"?"#00d08455":syncState==="error"?"#ff475755":"#ffa50255"}`,color:syncState==="done"?"#00d084":syncState==="error"?"#ff4757":"#ffa502",padding:"8px 16px",fontSize:9,letterSpacing:2,cursor:syncState==="syncing"?"not-allowed":"pointer",borderRadius:2,fontFamily:"'Courier New',monospace",width:"100%"}}>
              {syncState==="syncing"?`SINCRONIZANDO ${products.length} ITENS...`:syncState==="done"?"SINCRONIZADO":syncState==="error"?"ERRO — TENTE NOVAMENTE":"SINCRONIZAR AGORA"}
            </button>
          </div>
        </>}

        {/* ── PREVIEW FLUXO 1 ─────────────────────────────────────────────── */}
        {step==="preview_f1"&&<>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:8}}>
            <div>
              <p style={{fontSize:8,letterSpacing:3,color:"#00d084",marginBottom:2}}>FLUXO 1 — PREVIEW</p>
              <p style={{fontSize:11,color:"var(--text)",fontWeight:700}}>{rows.length} itens prontos para importação</p>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={cancelar} style={{background:"none",border:"1px solid var(--border2)",color:"var(--text4)",padding:"8px 16px",fontSize:9,letterSpacing:2,cursor:"pointer",borderRadius:2,fontFamily:"'Courier New',monospace"}}>CANCELAR</button>
              <button onClick={()=>setStep("confirm_f1")} style={{background:"#00d084",border:"none",color:"#000",padding:"8px 20px",fontSize:9,letterSpacing:2,cursor:"pointer",borderRadius:2,fontFamily:"'Courier New',monospace",fontWeight:700}}>PROSSEGUIR</button>
            </div>
          </div>

          {/* Erros de validação */}
          {erros.length>0&&<div style={{background:"#ff475712",border:"1px solid #ff475733",borderRadius:2,padding:"10px 14px",marginBottom:14}}>
            <p style={{fontSize:8,letterSpacing:2,color:"#ff4757",marginBottom:6}}>LINHAS IGNORADAS ({erros.length})</p>
            {erros.map((e,i)=><p key={i} style={{fontSize:9,color:"#ff4757",margin:"2px 0"}}>Linha {e.linha}: {e.msg}</p>)}
          </div>}

          {/* Tabela preview */}
          <PreviewTable rows={rows} showSku/>
        </>}

        {/* ── CONFIRMAÇÃO FLUXO 1 ─────────────────────────────────────────── */}
        {step==="confirm_f1"&&<>
          <div style={{maxWidth:480,margin:"60px auto 0",textAlign:"center"}}>
            <div style={{fontSize:36,color:"#ff4757",marginBottom:16}}>⚠</div>
            <p style={{fontSize:10,letterSpacing:3,color:"#ff4757",marginBottom:8}}>ATENÇÃO — OPERAÇÃO IRREVERSÍVEL</p>
            <p style={{fontSize:12,color:"var(--text)",marginBottom:8,lineHeight:1.6}}>
              Esta ação vai <strong>substituir todos os {products.length} produtos existentes</strong> pelos {rows.length} itens da planilha importada.
            </p>
            <p style={{fontSize:10,color:"var(--text3)",marginBottom:28,lineHeight:1.6}}>
              Esta é a contagem definitiva do inventário. A operação será registrada na auditoria e não pode ser desfeita.
            </p>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button onClick={()=>setStep("preview_f1")} style={{background:"none",border:"1px solid var(--border2)",color:"var(--text4)",padding:"10px 24px",fontSize:9,letterSpacing:2,cursor:"pointer",borderRadius:2,fontFamily:"'Courier New',monospace"}}>VOLTAR</button>
              <button onClick={confirmarFluxo1} style={{background:"#ff4757",border:"none",color:"#fff",padding:"10px 24px",fontSize:9,letterSpacing:2,cursor:"pointer",borderRadius:2,fontFamily:"'Courier New',monospace",fontWeight:700}}>CONFIRMAR IMPORTACAO</button>
            </div>
          </div>
        </>}

        {/* ── BLOQUEIO FLUXO 2 ────────────────────────────────────────────── */}
        {step==="bloqueio_f2"&&<>
          <div style={{maxWidth:480,margin:"60px auto 0",textAlign:"center"}}>
            <div style={{fontSize:36,color:"#ff4757",marginBottom:16}}>⛔</div>
            <p style={{fontSize:10,letterSpacing:3,color:"#ff4757",marginBottom:8}}>REIMPORTACAO BLOQUEADA</p>
            <p style={{fontSize:12,color:"var(--text)",marginBottom:8,lineHeight:1.6}}>
              Houve <strong>movimentações manuais</strong> no sistema após a data de exportação desta planilha.
            </p>
            <p style={{fontSize:10,color:"var(--text3)",marginBottom:8,lineHeight:1.6}}>
              Exportação original: <strong style={{color:"var(--text)",fontFamily:"'Courier New',monospace"}}>{fmtTs(f2Meta?.exportTs)}</strong>
            </p>
            <p style={{fontSize:10,color:"var(--text3)",marginBottom:28,lineHeight:1.6}}>
              Para garantir integridade, exporte uma nova planilha com o estado atual e repita o processo.
            </p>
            <button onClick={cancelar} style={{background:"none",border:"1px solid var(--border2)",color:"var(--text4)",padding:"10px 24px",fontSize:9,letterSpacing:2,cursor:"pointer",borderRadius:2,fontFamily:"'Courier New',monospace"}}>VOLTAR</button>
          </div>
        </>}

        {/* ── PREVIEW FLUXO 2 ─────────────────────────────────────────────── */}
        {step==="preview_f2"&&<>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:8}}>
            <div>
              <p style={{fontSize:8,letterSpacing:3,color:"#3b82f6",marginBottom:2}}>FLUXO 2 — REIMPORTACAO</p>
              <p style={{fontSize:11,color:"var(--text)",fontWeight:700}}>
                {rows.filter(r=>!r._bloqueado).length} alteracoes | {f2Meta?.ausentes?.length||0} ausentes
              </p>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={cancelar} style={{background:"none",border:"1px solid var(--border2)",color:"var(--text4)",padding:"8px 16px",fontSize:9,letterSpacing:2,cursor:"pointer",borderRadius:2,fontFamily:"'Courier New',monospace"}}>CANCELAR</button>
              <button onClick={confirmarFluxo2}
                disabled={rows.filter(r=>!r._bloqueado).length===0&&(f2Meta?.ausentes?.length||0)===0}
                style={{background:"#3b82f6",border:"none",color:"#fff",padding:"8px 20px",fontSize:9,letterSpacing:2,cursor:"pointer",borderRadius:2,fontFamily:"'Courier New',monospace",fontWeight:700}}>
                CONFIRMAR REIMPORTACAO
              </button>
            </div>
          </div>

          {/* Alterações */}
          {rows.length>0&&<>
            <p style={{fontSize:9,letterSpacing:3,color:"var(--text4)",marginBottom:8}}>ALTERACOES DETECTADAS</p>
            <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:20}}>
              {rows.map((r,i)=>(
                <div key={i} style={{background:r._bloqueado?"var(--bg)":"var(--bg2)",border:`1px solid ${r._bloqueado?"var(--border)":"#3b82f644"}`,borderRadius:2,padding:"10px 14px",opacity:r._bloqueado?0.5:1}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                    <div style={{flex:1,minWidth:0}}>
                      <span style={{fontSize:9,color:"#3b82f6",fontFamily:"'Courier New',monospace",fontWeight:700,marginRight:8}}>{r.sku}</span>
                      <span style={{fontSize:9,color:"var(--text)"}}>{r.name}</span>
                      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:4}}>
                        {r.diffs.map((d,j)=>(
                          <span key={j} style={{fontSize:8,color:"var(--text4)"}}>
                            <span style={{color:"var(--text5)"}}>{d.campo}: </span>
                            <span style={{color:"#ff4757",fontFamily:"'Courier New',monospace"}}>{String(d.de)}</span>
                            <span style={{color:"var(--text5)"}}> → </span>
                            <span style={{color:"#00d084",fontFamily:"'Courier New',monospace"}}>{String(d.para)}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                    <button onClick={()=>setRows(prev=>prev.map((x,idx)=>idx===i?{...x,_bloqueado:!x._bloqueado}:x))}
                      style={{background:"none",border:`1px solid ${r._bloqueado?"#00d08444":"#ff475744"}`,color:r._bloqueado?"#00d084":"#ff4757",padding:"3px 8px",fontSize:8,cursor:"pointer",borderRadius:2,letterSpacing:1,fontFamily:"'Courier New',monospace",flexShrink:0}}>
                      {r._bloqueado?"APLICAR":"IGNORAR"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>}

          {/* Ausentes */}
          {(f2Meta?.ausentes?.length||0)>0&&<>
            <p style={{fontSize:9,letterSpacing:3,color:"#ffa502",marginBottom:8}}>ITENS AUSENTES NA PLANILHA ({f2Meta.ausentes.length})</p>
            <div style={{background:"#ffa50210",border:"1px solid #ffa50233",borderRadius:2,padding:"10px 14px",marginBottom:8}}>
              <p style={{fontSize:9,color:"var(--text3)",lineHeight:1.5}}>
                Os itens abaixo existem no sistema mas não estão na planilha reimportada. Ao confirmar, serão marcados como <strong style={{color:"#ffa502"}}>NÃO CONTABILIZADOS</strong> e suas movimentações serão bloqueadas até resolução.
              </p>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:20}}>
              {f2Meta.ausentes.map((p,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px",background:"var(--bg2)",border:"1px solid #ffa50233",borderRadius:2}}>
                  <span style={{fontSize:9,color:"#ffa502",fontFamily:"'Courier New',monospace",fontWeight:700}}>{p.sku}</span>
                  <span style={{fontSize:9,color:"var(--text)",flex:1}}>{p.name}</span>
                  <span style={{fontSize:8,color:"#ffa502",letterSpacing:1}}>NAO CONTABILIZADO</span>
                </div>
              ))}
            </div>
          </>}

          {rows.length===0&&(f2Meta?.ausentes?.length||0)===0&&(
            <div style={{textAlign:"center",padding:"40px 0"}}>
              <div style={{fontSize:28,color:"#00d084",marginBottom:8}}>✓</div>
              <p style={{fontSize:11,color:"var(--text3)",letterSpacing:2}}>NENHUMA ALTERACAO DETECTADA</p>
              <p style={{fontSize:9,color:"var(--text4)",marginTop:4}}>A planilha está em sincronia com o sistema.</p>
            </div>
          )}
        </>}

        {/* ── PREVIEW FLUXO 3 ─────────────────────────────────────────────── */}
        {step==="preview_f3"&&<>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:8}}>
            <div>
              <p style={{fontSize:8,letterSpacing:3,color:"#a78bfa",marginBottom:2}}>FLUXO 3 — INCLUSAO EM MASSA</p>
              <p style={{fontSize:11,color:"var(--text)",fontWeight:700}}>{rows.filter(r=>!r._bloqueado).length} de {rows.length} itens serão incluídos</p>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={cancelar} style={{background:"none",border:"1px solid var(--border2)",color:"var(--text4)",padding:"8px 16px",fontSize:9,letterSpacing:2,cursor:"pointer",borderRadius:2,fontFamily:"'Courier New',monospace"}}>CANCELAR</button>
              <button onClick={confirmarFluxo3}
                disabled={rows.filter(r=>!r._bloqueado).length===0}
                style={{background:"#a78bfa",border:"none",color:"#fff",padding:"8px 20px",fontSize:9,letterSpacing:2,cursor:"pointer",borderRadius:2,fontFamily:"'Courier New',monospace",fontWeight:700}}>
                CONFIRMAR INCLUSAO
              </button>
            </div>
          </div>

          {erros.length>0&&<div style={{background:"#ff475712",border:"1px solid #ff475733",borderRadius:2,padding:"10px 14px",marginBottom:14}}>
            <p style={{fontSize:8,letterSpacing:2,color:"#ff4757",marginBottom:6}}>LINHAS IGNORADAS ({erros.length})</p>
            {erros.map((e,i)=><p key={i} style={{fontSize:9,color:"#ff4757",margin:"2px 0"}}>Linha {e.linha}: {e.msg}</p>)}
          </div>}

          {rows.some(r=>r._similar)&&<div style={{background:"#ffa50212",border:"1px solid #ffa50233",borderRadius:2,padding:"10px 14px",marginBottom:14}}>
            <p style={{fontSize:8,letterSpacing:2,color:"#ffa502",marginBottom:4}}>ITENS COM NOME SIMILAR DETECTADOS</p>
            <p style={{fontSize:9,color:"var(--text3)"}}>Revise os itens marcados com ⚠ antes de confirmar. Você pode bloqueá-los individualmente para não incluir.</p>
          </div>}

          <PreviewTableF3 rows={rows} setRows={setRows}/>
        </>}

      </div>
    </div>
  );
}

// ── Tabela Preview Fluxo 1 ────────────────────────────────────────────────────
function PreviewTable({rows,showSku}){
  return(
    <div style={{overflowX:"auto"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:9,fontFamily:"'Courier New',monospace"}}>
        <thead>
          <tr style={{borderBottom:"1px solid var(--border2)"}}>
            {showSku&&<th style={{padding:"6px 10px",textAlign:"left",color:"var(--text4)",letterSpacing:2,fontWeight:700,whiteSpace:"nowrap"}}>SKU</th>}
            <th style={{padding:"6px 10px",textAlign:"left",color:"var(--text4)",letterSpacing:2,fontWeight:700}}>DESCRICAO</th>
            <th style={{padding:"6px 10px",textAlign:"center",color:"#f97316",letterSpacing:2,fontWeight:700}}>CROMA</th>
            <th style={{padding:"6px 10px",textAlign:"center",color:"#3b82f6",letterSpacing:2,fontWeight:700}}>ESFERA</th>
            <th style={{padding:"6px 10px",textAlign:"right",color:"var(--text4)",letterSpacing:2,fontWeight:700}}>PRECO</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r,i)=>(
            <tr key={i} style={{borderBottom:"1px solid var(--border)",background:i%2===0?"transparent":"var(--bg2)"}}>
              {showSku&&<td style={{padding:"6px 10px",color:"#ffa502",whiteSpace:"nowrap"}}>{r.sku}</td>}
              <td style={{padding:"6px 10px",color:"var(--text)"}}>{r.name}</td>
              <td style={{padding:"6px 10px",textAlign:"center",color:"#f97316"}}>{r.croma}</td>
              <td style={{padding:"6px 10px",textAlign:"center",color:"#3b82f6"}}>{r.esfera}</td>
              <td style={{padding:"6px 10px",textAlign:"right",color:r.price>0?"var(--text3)":"#a78bfa"}}>
                {r.price>0?`R$ ${r.price.toFixed(2).replace(".",",")}` :"SEM PRECO"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Tabela Preview Fluxo 3 (com toggle de bloqueio) ───────────────────────────
function PreviewTableF3({rows,setRows}){
  function toggleBloqueio(i){
    setRows(prev=>prev.map((r,idx)=>idx===i?{...r,_bloqueado:!r._bloqueado}:r));
  }
  return(
    <div style={{overflowX:"auto"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:9,fontFamily:"'Courier New',monospace"}}>
        <thead>
          <tr style={{borderBottom:"1px solid var(--border2)"}}>
            <th style={{padding:"6px 10px",textAlign:"left",color:"var(--text4)",letterSpacing:2,fontWeight:700,whiteSpace:"nowrap"}}>SKU</th>
            <th style={{padding:"6px 10px",textAlign:"left",color:"var(--text4)",letterSpacing:2,fontWeight:700}}>DESCRICAO</th>
            <th style={{padding:"6px 10px",textAlign:"center",color:"#f97316",letterSpacing:2,fontWeight:700}}>CROMA</th>
            <th style={{padding:"6px 10px",textAlign:"center",color:"#3b82f6",letterSpacing:2,fontWeight:700}}>ESFERA</th>
            <th style={{padding:"6px 10px",textAlign:"right",color:"var(--text4)",letterSpacing:2,fontWeight:700}}>PRECO</th>
            <th style={{padding:"6px 10px",textAlign:"center",color:"var(--text4)",letterSpacing:2,fontWeight:700}}>ACAO</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r,i)=>(
            <tr key={i} style={{borderBottom:"1px solid var(--border)",background:r._bloqueado?"#ff475708":r._similar?"#ffa50208":"transparent",opacity:r._bloqueado?0.45:1}}>
              <td style={{padding:"6px 10px",color:"#a78bfa",whiteSpace:"nowrap"}}>{r.sku}</td>
              <td style={{padding:"6px 10px",color:"var(--text)"}}>
                {r._similar&&!r._bloqueado&&<span style={{color:"#ffa502",marginRight:4}}>⚠</span>}
                {r.name}
                {r._similar&&<div style={{fontSize:8,color:"#ffa502",marginTop:2}}>similar: "{r._similar.nome}" ({Math.round(r._similar.score*100)}%)</div>}
              </td>
              <td style={{padding:"6px 10px",textAlign:"center",color:"#f97316"}}>{r.croma}</td>
              <td style={{padding:"6px 10px",textAlign:"center",color:"#3b82f6"}}>{r.esfera}</td>
              <td style={{padding:"6px 10px",textAlign:"right",color:r.price>0?"var(--text3)":"#a78bfa"}}>
                {r.price>0?`R$ ${r.price.toFixed(2).replace(".",",")}` :"SEM PRECO"}
              </td>
              <td style={{padding:"6px 10px",textAlign:"center"}}>
                <button onClick={()=>toggleBloqueio(i)}
                  style={{background:"none",border:`1px solid ${r._bloqueado?"#00d08444":"#ff475744"}`,color:r._bloqueado?"#00d084":"#ff4757",padding:"3px 8px",fontSize:8,cursor:"pointer",borderRadius:2,letterSpacing:1,fontFamily:"'Courier New',monospace"}}>
                  {r._bloqueado?"INCLUIR":"IGNORAR"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}